import React, { createContext, useState, useEffect, useContext, useCallback } from "react";
import api from "../lib/api";
import { toast } from "react-toastify";

const AuthContext = createContext({});

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  const verifyToken = useCallback(async (token) => {
    try {
      // Limpa o token de possíveis espaços ou caracteres extras
      const cleanToken = token?.trim();
      if (!cleanToken) {
        console.log("Token não encontrado ou vazio");
        localStorage.removeItem("admin_token");
        setLoading(false);
        return;
      }

      // Configura o token antes de fazer a requisição
      api.defaults.headers.common["Authorization"] = `Bearer ${cleanToken}`;
      
      console.log("Verificando token...");
      const response = await api.get("/auth/verify");
      console.log("Resposta do verify:", response.data);
      
      // Validação da resposta
      if (!response || !response.data) {
        console.error("Resposta inválida do servidor:", response);
        localStorage.removeItem("admin_token");
        delete api.defaults.headers.common["Authorization"];
        setLoading(false);
        return;
      }
      
      const responseData = response.data;
      
      // Verifica se a resposta tem sucesso
      if (!responseData.success) {
        console.error("Token inválido ou expirado:", responseData);
        localStorage.removeItem("admin_token");
        delete api.defaults.headers.common["Authorization"];
        setLoading(false);
        return;
      }
      
      // Extrai admin do formato correto
      let admin;
      
      if (responseData.data && responseData.data.admin) {
        // Formato com wrapper: dados estão em responseData.data
        admin = responseData.data.admin;
      } else if (responseData.admin) {
        // Formato direto: dados estão em responseData
        admin = responseData.admin;
      } else {
        // Estrutura incompleta
        console.error("Estrutura de resposta incompleta:", responseData);
        localStorage.removeItem("admin_token");
        delete api.defaults.headers.common["Authorization"];
        setLoading(false);
        return;
      }
      
      // Valida se admin existe
      if (!admin) {
        console.error("Admin não encontrado na resposta:", responseData);
        localStorage.removeItem("admin_token");
        delete api.defaults.headers.common["Authorization"];
        setLoading(false);
        return;
      }
      
      // Configura admin e token
      setAdmin(admin);
      api.defaults.headers.common["Authorization"] = `Bearer ${cleanToken}`;
      console.log("Token verificado com sucesso, admin:", admin);
    } catch (error) {
      console.error("Erro ao verificar token:", error);
      console.error("Detalhes do erro:", {
        message: error.message,
        code: error.code,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
        baseURL: error.config?.baseURL
      });
      localStorage.removeItem("admin_token");
      delete api.defaults.headers.common["Authorization"];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Verifica se há token salvo
    const token = localStorage.getItem("admin_token");
    if (token) {
      // Configura token nas requisições antes de verificar
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      verifyToken(token);
    } else {
      setLoading(false);
    }
  }, [verifyToken]);

  const login = async (usuario, senha) => {
    try {
      const response = await api.post("/auth/login", { usuario, senha });
      
      // Validação da resposta
      if (!response || !response.data) {
        console.error("Resposta inválida do servidor:", response);
        toast.error("Resposta inválida do servidor. Tente novamente.");
        return { success: false, error: "Resposta inválida do servidor." };
      }
      
      // Suporta dois formatos de resposta:
      // 1. Formato direto: { success: true, token: "...", admin: {...} }
      // 2. Formato com wrapper: { success: true, data: { token: "...", admin: {...} }, message: "..." }
      const responseData = response.data;
      
      // Verifica se a resposta tem sucesso
      if (!responseData.success) {
        const errorMsg = responseData?.error || responseData?.message || "Login falhou.";
        console.error("Erro na resposta:", responseData);
        toast.error(errorMsg);
        return { success: false, error: errorMsg };
      }
      
      // Extrai token e admin do formato correto
      let token, admin;
      
      if (responseData.data && (responseData.data.token || responseData.data.admin)) {
        // Formato com wrapper: dados estão em responseData.data
        token = responseData.data.token;
        admin = responseData.data.admin;
      } else if (responseData.token || responseData.admin) {
        // Formato direto: dados estão em responseData
        token = responseData.token;
        admin = responseData.admin;
      } else {
        // Estrutura incompleta
        const errorMsg = responseData?.error || responseData?.message || "Resposta do servidor em formato inesperado.";
        console.error("Erro na resposta - estrutura incompleta:", responseData);
        toast.error(errorMsg);
        return { success: false, error: errorMsg };
      }
      
      // Valida se token e admin existem
      if (!token || !admin) {
        const errorMsg = "Token ou dados do administrador não encontrados na resposta.";
        console.error("Erro na resposta - dados faltando:", { token: !!token, admin: !!admin, responseData });
        toast.error(errorMsg);
        return { success: false, error: errorMsg };
      }
      
      // Salva token e configura admin
      localStorage.setItem("admin_token", token);
      setAdmin(admin);
      // Configura token nas requisições
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      const nomeAdmin = admin?.nome || admin?.usuario || "Administrador";
      toast.success(`Bem-vindo, ${nomeAdmin}!`);
      return { success: true };
    } catch (error) {
      console.error("Erro no login:", error);
      console.error("Detalhes:", {
        message: error.message,
        code: error.code,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        fullURL: error.config ? (error.config.baseURL || "") + (error.config.url || "") : "N/A"
      });
      
      let errorMessage = "Erro ao realizar login.";
      
      if (error.response?.status === 429) {
        // Rate limiting - muitas requisições
        const rateLimitMsg = error.response?.data?.error || "Muitas requisições. Tente novamente mais tarde.";
        errorMessage = `${rateLimitMsg} Aguarde alguns segundos antes de tentar novamente.`;
      } else if (error.message && error.message.includes("CORS")) {
        errorMessage = "Erro de CORS: O backend precisa permitir este domínio. Verifique a configuração do servidor.";
      } else if (error.code === "ERR_NETWORK" || error.message.includes("ERR_CONNECTION")) {
        errorMessage = "Erro de conexão: Não foi possível conectar ao servidor. Verifique se o backend está rodando em http://31.97.24.69:4015";
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.status === 401) {
        errorMessage = "Usuário ou senha incorretos.";
      } else if (error.response?.status === 500) {
        errorMessage = "Erro interno do servidor. Tente novamente mais tarde.";
      }
      
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    delete api.defaults.headers.common["Authorization"];
    setAdmin(null);
    toast.info("Logout realizado com sucesso!");
  };

  const value = {
    admin,
    loading,
    login,
    logout,
    isAuthenticated: !!admin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

